import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * CompareTray - a generic "tick 2-4 items and compare side-by-side" tray
 * that works across every Research & Compare catalogue (partners, house &
 * land packages, display homes, products).
 *
 * Single-kind at a time: adding an item of a different kind replaces the
 * tray (with a toast) so the compare table always compares like-with-like.
 * Persists to localStorage. Max 4 items.
 */

const STORAGE_KEY = 'bp_compare_tray_v1';
const MAX = 4;

const CompareTrayContext = createContext(null);

const load = () => {
  if (typeof window === 'undefined') return { kind: null, items: [] };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const v = raw ? JSON.parse(raw) : null;
    if (v && Array.isArray(v.items)) return { kind: v.kind || null, items: v.items.slice(0, MAX) };
  } catch { /* ignore */ }
  return { kind: null, items: [] };
};

export const CompareTrayProvider = ({ children }) => {
  const [tray, setTray] = useState(load);
  const [toast, setToast] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tray)); } catch { /* ignore */ }
  }, [tray]);

  const flash = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2600);
  }, []);

  const isInTray = useCallback(
    (id, kind) => tray.kind === kind && tray.items.some((i) => i.__id === String(id)),
    [tray],
  );

  const toggle = useCallback((item, kind, id) => {
    const key = String(id);
    const snap = { ...item, __id: key };
    setTray((prev) => {
      // Switching kinds → start fresh with this item.
      if (prev.kind && prev.kind !== kind) {
        flash(`Now comparing ${kind.replace('_', ' ')} — previous tray cleared`);
        return { kind, items: [snap] };
      }
      const exists = prev.items.some((i) => i.__id === key);
      if (exists) {
        const items = prev.items.filter((i) => i.__id !== key);
        return { kind: items.length ? kind : null, items };
      }
      if (prev.items.length >= MAX) {
        flash(`You can compare up to ${MAX} at once. Remove one first.`);
        return prev;
      }
      return { kind, items: [...prev.items, snap] };
    });
  }, [flash]);

  const remove = useCallback((id) => {
    setTray((prev) => {
      const items = prev.items.filter((i) => i.__id !== String(id));
      return { kind: items.length ? prev.kind : null, items };
    });
  }, []);

  const clear = useCallback(() => setTray({ kind: null, items: [] }), []);

  const value = useMemo(() => ({
    kind: tray.kind,
    items: tray.items,
    count: tray.items.length,
    max: MAX,
    isInTray,
    toggle,
    remove,
    clear,
    toast,
    open,
    setOpen,
  }), [tray, isInTray, toggle, remove, clear, toast, open]);

  return <CompareTrayContext.Provider value={value}>{children}</CompareTrayContext.Provider>;
};

export const useCompareTray = () => {
  const ctx = useContext(CompareTrayContext);
  if (!ctx) {
    return { kind: null, items: [], count: 0, max: MAX, isInTray: () => false, toggle: () => {}, remove: () => {}, clear: () => {}, toast: '', open: false, setOpen: () => {} };
  }
  return ctx;
};

export default CompareTrayContext;
